import { useCallback, useEffect, useState } from "react";
import { Coins, Image as ImageIcon, Heart, Ticket, Upload, ShoppingCart, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, brl, catName, errMsg, imgSrc, STATUS_LABEL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UploadDialog } from "../components/UploadDialog";
import { CheckoutDialog } from "../components/CheckoutDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_CLS = { pending: "bg-yellow-400/15 text-yellow-300", approved: "bg-lime-400/15 text-lime-300", rejected: "bg-red-500/15 text-red-300" };

const Stat = ({ icon: Icon, label, value, testId, accent = "text-cyan-300" }) => (
  <div className="glass rounded-xl p-6" data-testid={testId}>
    <Icon className={`h-5 w-5 ${accent}`} />
    <p className="mt-4 font-display text-4xl font-black">{value}</p>
    <p className="font-mono-tech text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
  </div>
);

const EnterDialog = ({ photo, onClose, onDone, onPay }) => {
  const [contests, setContests] = useState([]);
  useEffect(() => {
    if (photo) api.get("/contests").then((r) => setContests(r.data.filter((c) => c.status === "active" && (c.category === "todas" || c.category === photo.category))));
  }, [photo]);
  const enter = async (c) => {
    try {
      await api.post(`/photos/${photo.id}/enter`, { contest_id: c.id });
      toast.success(`Foto inscrita em ${c.title}`);
      onDone();
    } catch (e) {
      if (e.response?.status === 402) onPay(c);
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!photo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0E0E15]" data-testid="enter-contest-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-black uppercase">Inscrever em concurso</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {contests.length === 0 && <p className="text-sm text-slate-400">Nenhum concurso aberto para esta modalidade.</p>}
          {contests.map((c) => (
            <button key={c.id} data-testid={`enter-contest-option-${c.id}`} onClick={() => enter(c)} className="flex w-full items-center justify-between rounded-xl border border-white/10 p-4 text-left transition-colors hover:border-cyan-400">
              <span>
                <b className="font-display text-lg uppercase">{c.is_main && "★ "}{c.title}</b>
                <span className="block text-xs text-slate-400">{c.prize}</span>
              </span>
              <span className="font-mono-tech text-xs">{c.entry_fee > 0 ? brl(c.entry_fee) : "Grátis"}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MyPhoto = ({ p, onEnter, onDelete }) => (
  <div className="glass overflow-hidden rounded-xl" data-testid={`my-photo-${p.id}`}>
    <div className="relative h-48">
      <img src={imgSrc(p.src)} alt={p.title} className="h-full w-full object-cover" />
      <span className={`absolute left-2 top-2 rounded px-2 py-0.5 font-mono-tech text-[10px] uppercase ${STATUS_CLS[p.status]}`} data-testid={`my-photo-status-${p.id}`}>{STATUS_LABEL[p.status]}</span>
      {p.is_free && <span className="absolute right-2 top-2 rounded bg-lime-400 px-2 py-0.5 font-mono-tech text-[10px] font-bold uppercase text-black">Grátis</span>}
    </div>
    <div className="p-4">
      <p className="truncate font-display text-lg font-bold uppercase">{p.title}</p>
      <p className="font-mono-tech text-[11px] text-slate-500">{catName(p.category)} · <Heart className="inline h-3 w-3" /> {p.likes}</p>
      <p className="mt-1 text-xs text-cyan-300">{p.contest_id ? "Inscrita em concurso" : "Somente galeria"}</p>
      <div className="mt-3 flex gap-2">
        <button data-testid={`my-photo-enter-${p.id}`} onClick={() => onEnter(p)} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-cyan-400/40 py-1.5 text-xs hover:bg-cyan-400/10"><Send className="h-3.5 w-3.5" /> Concurso</button>
        <button data-testid={`my-photo-delete-${p.id}`} onClick={() => onDelete(p)} className="rounded-full border border-white/10 px-3 py-1.5 hover:border-red-400 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [txs, setTxs] = useState([]);
  const [upload, setUpload] = useState(false);
  const [checkout, setCheckout] = useState({ open: false, contest: null });
  const [entering, setEntering] = useState(null);

  const load = useCallback(() => {
    api.get("/me/photos").then((r) => setPhotos(r.data));
    api.get("/me/transactions").then((r) => setTxs(r.data));
    refreshUser();
  }, [refreshUser]);
  useEffect(() => {
    load();
  }, [load]);

  const del = async (p) => {
    if (!window.confirm("Remover esta foto?")) return;
    await api.delete(`/photos/${p.id}`);
    toast.success("Foto removida");
    load();
  };

  const likes = photos.reduce((s, p) => s + p.likes, 0);
  return (
    <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8" data-testid="user-dashboard">
      <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-400">// Meu painel</p>
          <h1 className="mt-2 font-display text-5xl font-black uppercase">Olá, {user.name.split(" ")[0]}</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="dashboard-upload-btn" onClick={() => setUpload(true)} className="btn-neon flex items-center gap-2 rounded-full px-6 py-3 font-semibold"><Upload className="h-4 w-4" /> Enviar foto</button>
          <button data-testid="btn-buy-extra-photos-dashboard" onClick={() => setCheckout({ open: true, contest: null })} className="flex items-center gap-2 rounded-full border border-lime-400/50 px-6 py-3 font-semibold text-lime-300 transition-colors hover:bg-lime-400/10"><ShoppingCart className="h-4 w-4" /> Comprar fotos</button>
        </div>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Coins} label="Créditos de foto" value={user.photo_credits} testId="stat-credits" accent="text-lime-300" />
        <Stat icon={ImageIcon} label="Fotos enviadas" value={photos.length} testId="stat-photos" />
        <Stat icon={Heart} label="Votos recebidos" value={likes} testId="stat-likes" accent="text-red-400" />
        <Stat icon={Ticket} label="Inscrições pagas" value={user.paid_contests?.length || 0} testId="stat-paid-contests" />
      </div>
      {user.free_credits > 0 && (
        <div className="mt-6 rounded-xl border border-lime-400/30 bg-lime-400/5 p-5 text-sm" data-testid="dashboard-free-photo-banner">
          Você ainda tem sua <b className="text-lime-300">foto grátis</b> disponível. Envie agora para a galeria ou um concurso aberto!
        </div>
      )}
      <h2 className="mb-6 mt-14 font-display text-3xl font-black uppercase">Minhas fotos</h2>
      {photos.length === 0 ? (
        <p className="text-slate-400" data-testid="dashboard-no-photos">Você ainda não enviou fotos.</p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {photos.map((p) => <MyPhoto key={p.id} p={p} onEnter={setEntering} onDelete={del} />)}
        </div>
      )}
      <h2 className="mb-6 mt-14 font-display text-3xl font-black uppercase">Pagamentos</h2>
      <div className="glass overflow-x-auto rounded-xl" data-testid="dashboard-transactions">
        {txs.length === 0 ? <p className="p-6 text-sm text-slate-400">Nenhum pagamento ainda.</p> : txs.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-4 border-b border-white/5 px-6 py-4 text-sm last:border-0">
            <span>{t.description}</span>
            <span className="font-mono-tech text-xs text-slate-500">{t.provider === "stripe" ? "Stripe" : "Mercado Pago"}</span>
            <span className={`font-mono-tech text-xs ${t.payment_status === "paid" ? "text-lime-300" : "text-yellow-300"}`}>{t.payment_status === "paid" ? "Pago" : "Pendente"}</span>
            <b>{brl(t.amount)}</b>
          </div>
        ))}
      </div>
      <UploadDialog open={upload} onOpenChange={setUpload} onUploaded={load} onNeedCredits={() => setCheckout({ open: true, contest: null })} />
      <CheckoutDialog open={checkout.open} contest={checkout.contest} onOpenChange={(o) => setCheckout((c) => ({ ...c, open: o }))} />
      <EnterDialog photo={entering} onClose={() => setEntering(null)} onDone={() => { setEntering(null); load(); }} onPay={(c) => { setEntering(null); setCheckout({ open: true, contest: c }); }} />
    </div>
  );
}
